(ns pfo.chat.tool.collection
  (:require [lentes.core :as l]
            [promesa.core :as p]
            [pfo.document.model :as document-model]
            [pfo.util :as util]
            [pfo.coltype.component :as coltype-component]
            [pfo.api :as api  :refer [process-error]]))

(defn- str->bool [x]
  (cond
    (boolean? x) x
    (nil? x)     false
    :else        (= "true" (.toLowerCase ^String (str x)))))

(defn- bool->str [b]
  (if (boolean? b) (str b) b))

(defn- to-keyword [arg]
  (cond
    (integer? arg) (keyword (str arg))
    (string? arg) (keyword arg)
    (keyword? arg) arg
    :else (throw (ex-info "Invalid argument type:" {:type (type arg)}))))

(defn- zero->nil [v] (when-not (= 0 v) v))

(defn normalize-ownership-entry
  "Treat explicit zero IDs as absent. The LLM routinely sends 0 placeholders
   for the unused owner field (see #2929)."
  [entry]
  (-> entry
      (update :owner_collection_id zero->nil)
      (update :owner_user_id zero->nil)
      (update :asset_collection_id zero->nil)))

(defn ownership-getter
  "Turn the raw ownership map into the “derived” shape that you posted."
  [raw]
  (let [own          (map normalize-ownership-entry (:ownerships raw))

        fam-members  (keep (fn [{:keys [owner_collection_id]}]
                             (when owner_collection_id
                               owner_collection_id))
                       own)
        fam-attrs    (into {}
                       (keep (fn [{:keys [owner_collection_id boi foreign voting]}]
                               (when owner_collection_id
                                 [(to-keyword owner_collection_id)
                                  {:boi    (bool->str boi)
                                   :foreign (bool->str foreign)
                                   :voting  (bool->str voting)}]))
                         own))
        fam-perc    (into {}
                      (keep (fn [{:keys [owner_collection_id ownership_value]}]
                              (when owner_collection_id
                                [(to-keyword owner_collection_id) ownership_value]))
                        own))
        fam-asset   (into {}
                      (keep (fn [{:keys [owner_collection_id asset_collection_id]}]
                              (when owner_collection_id
                                [(to-keyword owner_collection_id) asset_collection_id]))
                        own))

        fam-object   (into {}
                       (keep (fn [{:keys [owner_collection_id object]}]
                               (when owner_collection_id
                                 [(to-keyword owner_collection_id) object]))
                         own))
        member-members  (keep #(when (:owner_user_id %) (:owner_user_id %)) own)
        member-attrs    (into {}
                          (keep (fn [{:keys [owner_user_id boi foreign voting]}]
                                  (when owner_user_id
                                    [(to-keyword owner_user_id)
                                     {:boi    (bool->str boi)
                                      :foreign (bool->str foreign)
                                      :voting  (bool->str voting)}]))
                            own))

        member-perc    (into {}
                         (keep (fn [{:keys [owner_user_id ownership_value]}]
                                 (when owner_user_id
                                   [(to-keyword owner_user_id) ownership_value]))
                           own))
        member-object   (into {}
                          (keep (fn [{:keys [owner_user_id object]}]
                                  (when owner_user_id
                                    [(to-keyword owner_user_id) object]))
                            own))

        units       [(or (:ownership_unit raw) "percentage")]]

    {:family_members_attrs          fam-attrs
     :members_units_selection       units
     :ownership_document (:entity_documents raw)
     :ownership_document_objects (:entity_document_objects raw)
     :family_members_percentage     fam-perc
     :family_members                fam-members
     :family_members_asset_collection fam-asset
     :family_members_objects fam-object

     :members_attrs          member-attrs
     :members_percentage     member-perc
     :members                member-members
     :members_objects member-object}))

(defn to-int [arg]
  (cond
    (integer? arg) arg
    (string? arg) (js/parseInt arg)
    (keyword? arg) (js/parseInt (name arg))
    :else (throw (ex-info "Invalid argument type" {:type (type arg)}))))

(defn ownership-setter
  "Given the original raw map and a function `upd` that transforms the
   derived view, produce a new raw map.  The function `upd` receives the
   *derived* structure (the same shape produced by `ownership-getter`)."
  [raw upd]
  (let [derived   (ownership-getter raw)
        new-derived (upd derived)
        new-ownerships
        (concat
          (mapv (fn [owner-id]
                  (let [attrs   (get (:family_members_attrs new-derived) (to-keyword owner-id) {})
                        perc    (get (:family_members_percentage new-derived) (to-keyword owner-id))
                        asset   (get (:family_members_asset_collection new-derived) (to-keyword owner-id))
                        object  (get (:family_members_objects new-derived) (to-keyword owner-id))
                        asset-id-num  asset #_(some-> (not-empty asset) js/parseInt)]

                    {:owner_collection_id          owner-id
                     :boi                          (str->bool (:boi attrs))
                     :foreign                      (str->bool (:foreign attrs))
                     :voting                       (str->bool (:voting attrs))
                     :ownership_value              (or perc "0")
                     :asset_collection_id          asset-id-num
                     :object object
                     }))
            (:family_members new-derived))
          (mapv (fn [owner-id]
                  (let [attrs   (get (:members_attrs new-derived) (to-keyword owner-id) {})
                        perc    (get (:members_percentage new-derived) (to-keyword owner-id))
                        asset   (get (:members_asset_collection new-derived) (to-keyword owner-id))
                        object  (get (:members_objects new-derived) (to-keyword owner-id))
                        asset-id-num asset #_(some-> (not-empty asset) js/parseInt)]
                    {:owner_user_id          owner-id
                     :boi                          (str->bool (:boi attrs))
                     :foreign                      (str->bool (:foreign attrs))
                     :voting                       (str->bool (:voting attrs))
                     :ownership_value              (or perc "0")
                     :asset_collection_id          asset-id-num
                     :object object}))
            (:members new-derived)))]

    (-> raw
      (assoc :ownerships new-ownerships)
      (assoc :ownership_unit (first (:members_units_selection new-derived)))
      (assoc :entity_documents (:ownership_document new-derived))
      (assoc :entity_document_objects (:ownership_document_objects new-derived)))))

(def ownership-lens
  "A lens that converts between the raw ownership map and the derived
   representation you posted.  It is fully bidirectional and can be used
   with `l/focus`, `l/over` or `l/put`."
  (l/lens ownership-getter ownership-setter))

(comment
  (def raw-data
    {:collection_id 187
     :entity_documents [629 2365]
     :ownership_unit "percentage"
     :ownerships [{:asset_collection_id 132
                   :boi true
                   :foreign false
                   :owner_collection_id 52
                   :ownership_value "10"
                   :voting false}
                  {:asset_collection_id 1357
                   :boi true
                   :foreign false
                   :owner_collection_id 53
                   :ownership_value "55"
                   :voting true}
                  {:asset_collection_id 1356
                   :boi false
                   :foreign false
                   :owner_collection_id 55
                   :ownership_value "35"
                   :voting true}
                  {:boi false
                   :foreign false
                   :owner_collection_id 772
                   :owner_user_id 771
                   :ownership_value "2000"
                   :voting true}]})

  (def derived (l/focus ownership-lens raw-data))
  derived

  (def updated-derived
    (l/over ownership-lens
      (fn [d]
        (-> d
          (assoc-in [:family_members_percentage 52] "15")
          (assoc-in [:family_members_attrs 52 :boi] "false")))
      raw-data))

  (:ownerships updated-derived)
  (def src-atom (atom raw-data))
  (def view-atom (l/derive ownership-lens src-atom))

  @view-atom

  (swap! view-atom update :family_members conj 160)
  (swap! view-atom assoc-in [:family_members_percentage 160] "333")

  (count (:ownerships raw-data))
  (count (:ownerships @src-atom)))


;; ── Carried Interest lens ────────────────────────────────────────────────────
;; Raw shape (mirrors UpdateCollectionCarriedInterestOwnershipInput):
;;   {:collection_id           <int>
;;    :entity_documents        [<doc-id> ...]
;;    :use_percentage          <bool>
;;    :ownerships              [{:owner_collection_id <int>|nil
;;                               :owner_user_id       <int>|nil
;;                               :ownership_value     <str>|nil
;;                               :boi                 <bool>|nil
;;                               :asset_collection_id <int>|nil}]}
;;
;; Derived shape (keys match field refs used by the coltype form):
;;   {:carried_interest_document                  [<doc-id> ...]
;;    :carried_interest_use_percentage             <bool>
;;    :carried_interest_members_unit_selection     ["percentage"]
;;    :carried_interest_family_members             [<col-id> ...]
;;    :carried_interest_family_members_attrs       {<col-id-kw> {:boi "true"|"false"}}
;;    :carried_interest_family_members_percentage  {<col-id-kw> <str>}
;;    :carried_interest_family_members_asset_collection {<col-id-kw> <asset-col-id>}
;;    :carried_interest_family_members_objects     {}
;;    :carried_interest_members                   [<user-id> ...]
;;    :carried_interest_members_attrs             {<user-id-kw> {:boi "true"|"false"}}
;;    :carried_interest_members_percentage        {<user-id-kw> <str>}
;;    :carried_interest_members_objects           {}}

(defn carried-interest-getter
  "Turn a raw carried-interest map into the derived coltype-field shape."
  [raw]
  (let [own (or (:ownerships raw) [])]
    {:carried_interest_document                  (or (:entity_documents raw) [])
     :carried_interest_use_percentage            (or (:use_percentage raw) false)
     :carried_interest_members_unit_selection    ["percentage"]

     :carried_interest_family_members
     (keep :owner_collection_id own)

     :carried_interest_family_members_attrs
     (into {}
       (keep (fn [{:keys [owner_collection_id boi carried_interest_owner_type]}]
               (when owner_collection_id
                 [(to-keyword owner_collection_id) {:boi                          (bool->str boi)
                                                    :carried_interest_owner_type  carried_interest_owner_type}]))
         own))

     :carried_interest_family_members_percentage
     (into {}
       (keep (fn [{:keys [owner_collection_id ownership_value]}]
               (when owner_collection_id
                 [(to-keyword owner_collection_id) ownership_value]))
         own))

     :carried_interest_family_members_asset_collection
     (into {}
       (keep (fn [{:keys [owner_collection_id asset_collection_id]}]
               (when owner_collection_id
                 [(to-keyword owner_collection_id) asset_collection_id]))
         own))

     ;; Populated from per-entry :object field so that CollectionField can
     ;; seed its shared-atom choices on mount (same pattern as ownership-getter).
     :carried_interest_family_members_objects
     (into {}
       (keep (fn [{:keys [owner_collection_id object]}]
               (when owner_collection_id
                 [(to-keyword owner_collection_id) object]))
         own))

     :carried_interest_members
     (keep :owner_user_id own)

     :carried_interest_members_attrs
     (into {}
       (keep (fn [{:keys [owner_user_id boi carried_interest_owner_type]}]
               (when owner_user_id
                 [(to-keyword owner_user_id) {:boi                         (bool->str boi)
                                              :carried_interest_owner_type carried_interest_owner_type}]))
         own))

     :carried_interest_members_percentage
     (into {}
       (keep (fn [{:keys [owner_user_id ownership_value]}]
               (when owner_user_id
                 [(to-keyword owner_user_id) ownership_value]))
         own))

     ;; Same pattern: populated from per-entry :object field.
     :carried_interest_members_objects
     (into {}
       (keep (fn [{:keys [owner_user_id object]}]
               (when owner_user_id
                 [(to-keyword owner_user_id) object]))
         own))}))

(defn carried-interest-setter
  "Given raw map and an update fn over the derived shape, produce a new raw map."
  [raw upd]
  (let [derived     (carried-interest-getter raw)
        new-derived (upd derived)
        new-ownerships
        (concat
          (mapv (fn [owner-id]
                  (let [attrs  (get (:carried_interest_family_members_attrs new-derived) (to-keyword owner-id) {})
                        perc   (get (:carried_interest_family_members_percentage new-derived) (to-keyword owner-id))
                        asset  (get (:carried_interest_family_members_asset_collection new-derived) (to-keyword owner-id))
                        object (get (:carried_interest_family_members_objects new-derived) (to-keyword owner-id))]
                    {:owner_collection_id           owner-id
                     :boi                          (str->bool (:boi attrs))
                     :carried_interest_owner_type  (:carried_interest_owner_type attrs)
                     :ownership_value              (or perc "0")
                     :asset_collection_id          asset
                     :object                       object}))
            (:carried_interest_family_members new-derived))
          (mapv (fn [owner-id]
                  (let [attrs  (get (:carried_interest_members_attrs new-derived) (to-keyword owner-id) {})
                        perc   (get (:carried_interest_members_percentage new-derived) (to-keyword owner-id))
                        object (get (:carried_interest_members_objects new-derived) (to-keyword owner-id))]
                    {:owner_user_id                owner-id
                     :boi                         (str->bool (:boi attrs))
                     :carried_interest_owner_type (:carried_interest_owner_type attrs)
                     :ownership_value             (or perc "0")
                     :object                      object}))
            (:carried_interest_members new-derived)))]
    (-> raw
      (assoc :ownerships new-ownerships)
      (assoc :use_percentage (:carried_interest_use_percentage new-derived))
      (assoc :entity_documents (:carried_interest_document new-derived)))))

(def carried-interest-lens
  "Bidirectional lens between raw carried-interest map and derived coltype-field shape."
  (l/lens carried-interest-getter carried-interest-setter))

;; ── End carried interest lens ─────────────────────────────────────────────────

;; ── Trust Beneficial Interest lens ──────────────────────────────────────────
;; Raw shape (mirrors UpdateCollectionTrustBeneficialInterestInput):
;;   {:collection_id           <int>
;;    :entity_documents        [<doc-id> ...]          ; stored as boi_document on server
;;    :primary_ownerships      [{:owner_collection_id <int>|nil
;;                               :owner_user_id       <int>|nil
;;                               :boi                 <bool>|nil
;;                               :asset_collection_id <int>|nil}]
;;    :secondary_ownerships    [{...same...}]}
;;
;; Derived shape (keys match field refs used by the coltype form):
;;   {:boi_document                                    [<doc-id> ...]
;;    :primary_family_beneficiaries                    [<col-id> ...]
;;    :primary_family_beneficiaries_attrs              {<col-id-kw> {:boi "true"|"false"}}
;;    :primary_family_beneficiaries_asset_collection   {<col-id-kw> <asset-col-id>}
;;    :primary_family_beneficiaries_objects            {}
;;    :primary_beneficiaries                           [<user-id> ...]
;;    :primary_beneficiaries_attrs                     {<user-id-kw> {:boi "true"|"false"}}
;;    :primary_beneficiaries_objects                   {}
;;    :secondary_family_beneficiaries                  [<col-id> ...]
;;    :secondary_family_beneficiaries_attrs            {<col-id-kw> {:boi "true"|"false"}}
;;    :secondary_family_beneficiaries_asset_collection {<col-id-kw> <asset-col-id>}
;;    :secondary_family_beneficiaries_objects          {}
;;    :secondary_beneficiaries                         [<user-id> ...]
;;    :secondary_beneficiaries_attrs                   {<user-id-kw> {:boi "true"|"false"}}
;;    :secondary_beneficiaries_objects                 {}}

(defn- beneficiary-entries->derived
  "Extract derived keys for one beneficiary role (primary or secondary).
   `entries` is the sequence of ownership maps from the raw input.
   `fam-prefix` is e.g. \"primary_family_beneficiaries\", `usr-prefix` is e.g. \"primary_beneficiaries\"."
  [entries fam-prefix usr-prefix]
  (let [valid-id? (fn [id] (and id (not= id 0)))]
    {(keyword fam-prefix)
     (keep #(when (valid-id? (:owner_collection_id %)) (:owner_collection_id %)) entries)

     (keyword (str fam-prefix "_attrs"))
     (into {}
       (keep (fn [{:keys [owner_collection_id boi]}]
               (when (valid-id? owner_collection_id)
                 [(to-keyword owner_collection_id) {:boi (bool->str boi)}]))
         entries))

     (keyword (str fam-prefix "_asset_collection"))
     (into {}
       (keep (fn [{:keys [owner_collection_id asset_collection_id]}]
               (when (valid-id? owner_collection_id)
                 [(to-keyword owner_collection_id) asset_collection_id]))
         entries))

     (keyword (str fam-prefix "_objects"))
     (into {}
       (keep (fn [{:keys [owner_collection_id object]}]
               (when (valid-id? owner_collection_id)
                 [(to-keyword owner_collection_id) object]))
         entries))

     (keyword usr-prefix)
     (keep #(when (valid-id? (:owner_user_id %)) (:owner_user_id %)) entries)

     (keyword (str usr-prefix "_attrs"))
     (into {}
       (keep (fn [{:keys [owner_user_id boi]}]
               (when (valid-id? owner_user_id)
                 [(to-keyword owner_user_id) {:boi (bool->str boi)}]))
         entries))

     (keyword (str usr-prefix "_objects"))
     (into {}
       (keep (fn [{:keys [owner_user_id object]}]
               (when (valid-id? owner_user_id)
                 [(to-keyword owner_user_id) object]))
         entries))}))

(defn trust-beneficial-interest-getter
  "Turn a raw trust-beneficial-interest map into the derived coltype-field shape."
  [raw]
  (let [primary   (or (:primary_ownerships raw) [])
        secondary (or (:secondary_ownerships raw) [])]
    (merge
      {:boi_document         (or (:entity_documents raw) [])
       :boi_document_objects (or (:entity_document_objects raw) {})}
      (beneficiary-entries->derived primary   "primary_family_beneficiaries"   "primary_beneficiaries")
      (beneficiary-entries->derived secondary "secondary_family_beneficiaries" "secondary_beneficiaries"))))

(defn- derived->beneficiary-entries
  "Reconstruct raw ownership entry list for one role from the derived map.
   `fam-prefix` e.g. \"primary_family_beneficiaries\", `usr-prefix` e.g. \"primary_beneficiaries\"."
  [derived fam-prefix usr-prefix]
  (let [fam-k       (keyword fam-prefix)
        fam-attrs-k (keyword (str fam-prefix "_attrs"))
        fam-asset-k (keyword (str fam-prefix "_asset_collection"))
        fam-obj-k   (keyword (str fam-prefix "_objects"))
        usr-k       (keyword usr-prefix)
        usr-attrs-k (keyword (str usr-prefix "_attrs"))
        usr-obj-k   (keyword (str usr-prefix "_objects"))]
    (concat
      (mapv (fn [owner-id]
              (let [attrs  (get (fam-attrs-k derived) (to-keyword owner-id) {})
                    asset  (get (fam-asset-k derived) (to-keyword owner-id))
                    object (get (fam-obj-k derived) (to-keyword owner-id))]
                {:owner_collection_id owner-id
                 :boi                 (str->bool (:boi attrs))
                 :asset_collection_id asset
                 :object              object}))
            (fam-k derived))
      (mapv (fn [owner-id]
              (let [attrs  (get (usr-attrs-k derived) (to-keyword owner-id) {})
                    object (get (usr-obj-k derived) (to-keyword owner-id))]
                {:owner_user_id owner-id
                 :boi           (str->bool (:boi attrs))
                 :object        object}))
            (usr-k derived)))))

(defn trust-beneficial-interest-setter
  "Given raw map and an update fn over the derived shape, produce a new raw map."
  [raw upd]
  (let [derived     (trust-beneficial-interest-getter raw)
        new-derived (upd derived)]
    (-> raw
      (assoc :primary_ownerships      (derived->beneficiary-entries new-derived "primary_family_beneficiaries"   "primary_beneficiaries"))
      (assoc :secondary_ownerships    (derived->beneficiary-entries new-derived "secondary_family_beneficiaries" "secondary_beneficiaries"))
      (assoc :entity_documents        (:boi_document new-derived))
      (assoc :entity_document_objects (:boi_document_objects new-derived)))))

(def trust-beneficial-interest-lens
  "Bidirectional lens between raw trust-beneficial-interest map and derived coltype-field shape."
  (l/lens trust-beneficial-interest-getter trust-beneficial-interest-setter))

;; ── End trust beneficial interest lens ───────────────────────────────────────

(defn get-all-doc-fields [all-flattened-fields ref-kw-set]
  (filter
   (fn [field]
     (= (:type field) "document"))
   all-flattened-fields))

(defn populate-document-objects! [doc-fields col-data document-field-objects-map-cb]
  (let [field-document-ids (into {}(for [field doc-fields
                                         :let [ref (keyword (:ref field))
                                               ids (col-data ref)]
                                         :when (seq ids)]
                                     [ref ids]))
        all-document-ids (mapcat second field-document-ids)]
    ;; TODO: optimize: don't load documents previously loaded
    (->>
      (p/all (mapv (fn [id] (document-model/document-details id)) all-document-ids))
      (p/map
        (fn [documents]
          (let [document-id-map (util/associate-by :id documents)
                document-field-objects-map (into {}
                                             (map (fn [field]
                                                    [(keyword (coltype-component/get-objects-ref field))
                                                     (into {}(map (fn [id]
                                                                    [id (document-id-map id)])
                                                               (field-document-ids (keyword (:ref field)))))])
                                               doc-fields))]
            (document-field-objects-map-cb document-field-objects-map))))
      (process-error))))
